import {
  BinderAccessNode,
  ChildResourceAccess,
  OrganizationAccessNode,
  ResourceOrganization,
  TenderAccessNode,
  UserAccessGroups,
  UserResourceAccessResponse
} from './userResourceAccess.types';

// Resources can come back without a name, so unnamed ones sort last.
const sortByName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    if (!a.name) {
      return b.name ? 1 : 0;
    }
    if (!b.name) {
      return -1;
    }
    return a.name.localeCompare(b.name);
  });

const toOrganization = (
  resource: ChildResourceAccess
): ResourceOrganization => ({
  id: resource.organization?.id || '',
  name: resource.organization?.name || ''
});

const toBinderNode = (binder: ChildResourceAccess): BinderAccessNode => ({
  id: binder.id,
  name: binder.name,
  organization: toOrganization(binder),
  participantType: binder.participantType,
  isLastAdmin: binder.isLastAdmin === true
});

const toTenderNode = (tender: ChildResourceAccess): TenderAccessNode => ({
  id: tender.id,
  name: tender.name,
  organization: toOrganization(tender),
  participantType: tender.participantType,
  isLastAdmin: tender.isLastAdmin === true
});

// Binders and tenders can belong to an organization the user has no role in,
// which still gets a group so every resource stays under its organization.
const getOrCreateOrganization = (
  organizations: Map<string, OrganizationAccessNode>,
  resource: ChildResourceAccess
): OrganizationAccessNode => {
  const { id, name } = toOrganization(resource);
  const existing = organizations.get(id);
  if (existing) {
    return existing;
  }
  const created: OrganizationAccessNode = {
    id,
    name: name || 'Unknown organization',
    hasDirectAccess: false,
    isLastOwner: false,
    binders: [],
    tenders: []
  };
  organizations.set(id, created);
  return created;
};

export const groupUserResourceAccess = (
  response: UserResourceAccessResponse
): UserAccessGroups => {
  const organizations = new Map<string, OrganizationAccessNode>();

  (response.organizations || []).forEach((organization) => {
    organizations.set(organization.id, {
      id: organization.id,
      name: organization.name,
      hasDirectAccess: true,
      participantType: organization.participantType,
      isLastOwner: organization.isLastOwner === true,
      binders: [],
      tenders: []
    });
  });

  (response.binders || []).forEach((binder) => {
    getOrCreateOrganization(organizations, binder).binders.push(
      toBinderNode(binder)
    );
  });

  (response.tenders || []).forEach((tender) => {
    getOrCreateOrganization(organizations, tender).tenders.push(
      toTenderNode(tender)
    );
  });

  const sorted = sortByName(Array.from(organizations.values())).map(
    (organization) => ({
      ...organization,
      binders: sortByName(organization.binders),
      tenders: sortByName(organization.tenders)
    })
  );

  return {
    direct: sorted.filter((organization) => organization.hasDirectAccess),
    indirect: sorted.filter((organization) => !organization.hasDirectAccess)
  };
};
